import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { CompanyLogoQuery } from "../../../generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const companyLogoQuery = graphql(`
  query companyLogo($companyGuid: String!) {
    companyLogoUrl(companyGuid: $companyGuid)
  }
`);

/**
 * Fetches the CDN URL of a company's logo (the one Stotles has on file), by company guid.
 *
 * Backed by the `@Public()` `companyLogoUrl` app-service query, so it works without a signed-in
 * user — report headers render the owning company's logo on reports that may be viewed anonymously.
 *
 * @param companyGuid - The guid of the company whose logo to fetch.
 * @param options - Optional react-query overrides.
 * @returns `{ data }` is the logo URL, or `undefined` when the company has no logo on file.
 */
export function useCompanyLogoUrl(
  companyGuid: string,
  options?: UseQueryOptions<CompanyLogoQuery, unknown, CompanyLogoQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["companyLogo", companyGuid],
    companyLogoQuery,
    [{ companyGuid }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return { data: data?.companyLogoUrl ?? undefined, ...rest };
}
